import type { BusinessLicense, PromoLicense } from '@/entities/ocr/api/ocr.api';
import {
  BUSINESS_OWNER_TYPE,
  type BusinessOwnerType,
  type PlatformUserDetailUpdateRequest,
  type PlatformUserTermsAgreementItem,
  type TermsAgreementItem,
} from '@/entities/user/api/user.api';
import { formatBusinessRegistrationNumber } from '@/shared/utils/businessRegistrationNumber';

/** 레거시 `userDetailInfo.js` termLabels */
export const termLabel = (termId: string): string => {
  switch (termId) {
    case 'termsOfService':
      return '이용약관';
    case 'privacyPolicyConsent':
      return '개인정보처리방침';
    case 'complianceAgreement':
      return '법령 준수 서약서';
    default:
      return termId;
  }
};

/** 약관 동의 표의 한 행. 신·구 응답을 같은 모양으로 맞춘다 */
export interface TermsRow {
  key: string;
  name: string;
  agreed: boolean | null;
  /** `yyyy-MM-dd HH:mm:ss`. 레거시 응답에는 없다 */
  agreedAt: string | null;
  /** 동의한 버전이 지금 시행 중인 버전이 아니면 재동의 대상 */
  needsReagree: boolean;
}

/**
 * 종류별 동의 정보(termsAgreementItems)를 쓰고, 없으면 레거시 JSON 묶음으로 떨어진다.
 * 레거시에는 종류별 일시·버전이 없어 일시가 비고 재동의 판정도 하지 않는다.
 */
export const resolveTermsRows = (
  items: PlatformUserTermsAgreementItem[] | undefined,
  legacy: TermsAgreementItem[],
): TermsRow[] => {
  if (items?.length) {
    return items.map((item) => ({
      key: item.type,
      name: item.name || termLabel(item.type),
      agreed: item.agreed,
      agreedAt: item.agreedAt,
      needsReagree: item.agreed === true && item.currentVersion === false,
    }));
  }
  return legacy.map((item) => ({
    key: item.termId,
    name: termLabel(item.termId),
    agreed: item.agreed,
    agreedAt: null,
    needsReagree: false,
  }));
};

/**
 * 레거시 getSalesApprovalEligibilityFromVerificationFields
 * 사업자·금융·판촉 인증 상태로 거래/정산 가능 여부를 계산한다.
 */
export const getSalesApprovalEligibility = (
  businessVerificationStatus?: string,
  financialVerificationStatus?: string,
  promoLicenseStatus?: string,
) => {
  const businessPendingOrApproved =
    businessVerificationStatus === 'PENDING' ||
    businessVerificationStatus === 'APPROVED';
  // 레거시: NOT_VERIFIED | VERIFIED 일 때 거래 가능 조건에 포함
  const financialOk =
    financialVerificationStatus === 'NOT_VERIFIED' ||
    financialVerificationStatus === 'VERIFIED';
  const promoPendingOrApproved =
    promoLicenseStatus === 'PENDING' || promoLicenseStatus === 'APPROVED';

  const isTransactionEnabled =
    businessPendingOrApproved && financialOk && promoPendingOrApproved;

  const isSettlementEnabled =
    businessVerificationStatus === 'APPROVED' &&
    financialVerificationStatus === 'VERIFIED' &&
    promoLicenseStatus === 'APPROVED';

  let derived: PlatformUserDetailUpdateRequest['salesApprovalStatus'] =
    'TRANSACTION_IMPOSSIBLE';
  if (isSettlementEnabled) {
    derived = 'SETTLEMENT_POSSIBLE';
  } else if (isTransactionEnabled) {
    derived = 'TRANSACTION_POSSIBLE';
  }

  return { isTransactionEnabled, isSettlementEnabled, derived };
};

/**
 * 레거시 userDetailInfo 저장: trainingStatus는 UI에서 편집하지 않아도
 * form ?? detail ?? NOT_REGISTERED 순으로 항상 PUT body에 포함한다.
 */
export const resolveTrainingStatusForSave = (
  formOrCurrent?: string | null,
  detailFallback?: string | null,
): NonNullable<PlatformUserDetailUpdateRequest['trainingStatus']> => {
  const value = formOrCurrent ?? detailFallback ?? 'NOT_REGISTERED';
  return value as NonNullable<
    PlatformUserDetailUpdateRequest['trainingStatus']
  >;
};

export const businessOwnerTypeLabel = (
  type: string | null | undefined,
): string => {
  if (type === BUSINESS_OWNER_TYPE.INDIVIDUAL) return '개인사업자';
  if (type === BUSINESS_OWNER_TYPE.CORPORATE) return '법인사업자';
  return '미등록';
};

export const isIndividualBusinessOwner = (
  type: string | null | undefined,
): type is typeof BUSINESS_OWNER_TYPE.INDIVIDUAL =>
  type === BUSINESS_OWNER_TYPE.INDIVIDUAL;

const pad2 = (value: number): string => String(value).padStart(2, '0');

export const toIsoDateToday = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;

export const isPastIsoDate = (iso: string, now: Date = new Date()): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(iso) && iso < toIsoDateToday(now);

/**
 * OCR 생년월일(yyMMdd) → 수정 API 형식(yyyy-MM-dd).
 * 연도 두 자리가 현재 연도 두 자리보다 크면 1900년대.
 * 변환 결과가 오늘 이후면 1900년대로 되돌린다(PUT 는 과거만 허용).
 */
export const ocrBirthDateToIso = (
  yyMMdd: string | null | undefined,
  now: Date = new Date(),
): string | null => {
  const digits = yyMMdd?.replace(/\D/g, '') ?? '';
  if (!/^\d{6}$/.test(digits)) return null;
  const yy = Number(digits.slice(0, 2));
  const mm = digits.slice(2, 4);
  const dd = digits.slice(4, 6);
  const month = Number(mm);
  const day = Number(dd);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const currentYy = now.getFullYear() % 100;
  const century = yy > currentYy ? 1900 : 2000;
  let iso = `${century + yy}-${mm}-${dd}`;
  if (!isPastIsoDate(iso, now)) {
    iso = `${1900 + yy}-${mm}-${dd}`;
  }
  return isPastIsoDate(iso, now) ? iso : null;
};

/** OCR/ISO 날짜 문자열을 수정 API 형식(yyyy-MM-dd)으로 맞춘다. */
export const ocrDateToIso = (value?: string | null): string | null => {
  const raw = value?.trim() ?? '';
  if (!raw) return null;
  const digits = raw.replace(/\D/g, '');
  if (!/^\d{8}$/.test(digits)) return null;
  const iso = `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  const month = Number(iso.slice(5, 7));
  const day = Number(iso.slice(8, 10));
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return iso;
};

export type PromoLicenseOcrFormPatch = {
  promoLicenseNumber?: string;
  promoLicenseValidFrom?: string;
  promoLicenseValidTo?: string;
};

/**
 * 어드민 판촉 신고증 OCR 응답을 폼 패치로 바꾼다.
 * 이 API 는 저장하지 않으므로 호출 측이 PUT 으로 저장한다.
 */
export const mapPromoLicenseOcrToForm = (
  ocr: PromoLicense,
): PromoLicenseOcrFormPatch => {
  const patch: PromoLicenseOcrFormPatch = {};
  if (ocr.registrationNumber?.trim()) {
    patch.promoLicenseNumber = ocr.registrationNumber.trim();
  }
  const validFrom = ocrDateToIso(ocr.registrationStartDate);
  if (validFrom) patch.promoLicenseValidFrom = validFrom;
  const validTo = ocrDateToIso(ocr.registrationEndDate);
  if (validTo) patch.promoLicenseValidTo = validTo;
  return patch;
};

export type BusinessLicenseOcrFormPatch = {
  companyName?: string;
  ceoName?: string;
  address?: string;
  taxInvoiceEmail?: string;
  businessLicenseNumber?: string;
  /** 인식 실패면 생략 — 폼 유형을 바꾸지 않는다 */
  businessOwnerType?: BusinessOwnerType;
  /** 유형을 인식했을 때만. 법인이면 빈 문자열 */
  birthDate?: string;
};

/**
 * 어드민 OCR 응답을 사업자 정보 폼 패치로 바꾼다.
 * 이 API 는 저장하지 않으므로 호출 측이 PUT 으로 저장한다.
 */
export const mapBusinessLicenseOcrToForm = (
  ocr: BusinessLicense,
  now: Date = new Date(),
): BusinessLicenseOcrFormPatch => {
  const patch: BusinessLicenseOcrFormPatch = {};
  if (ocr.businessName?.trim()) patch.companyName = ocr.businessName.trim();
  if (ocr.representativeName?.trim()) {
    patch.ceoName = ocr.representativeName.trim();
  }
  if (ocr.address?.trim()) patch.address = ocr.address.trim();
  if (ocr.taxEmail?.trim()) patch.taxInvoiceEmail = ocr.taxEmail.trim();
  if (ocr.registrationNumber?.trim()) {
    patch.businessLicenseNumber = formatBusinessRegistrationNumber(
      ocr.registrationNumber,
    );
  }

  if (
    ocr.businessOwnerType === BUSINESS_OWNER_TYPE.INDIVIDUAL ||
    ocr.businessOwnerType === BUSINESS_OWNER_TYPE.CORPORATE
  ) {
    patch.businessOwnerType = ocr.businessOwnerType;
    patch.birthDate = isIndividualBusinessOwner(ocr.businessOwnerType)
      ? (ocrBirthDateToIso(ocr.birthDate, now) ?? '')
      : '';
  }

  return patch;
};

/**
 * PUT 에 실을 사업자 유형·생년월일.
 * 미등록(빈 값)이면 둘 다 생략해 기존 DB 값을 유지한다.
 * 법인이면 생년월일을 보내지 않는다(서버가 null 로 비움).
 */
export const buildBusinessOwnerSaveFields = (form: {
  businessOwnerType: string;
  birthDate: string;
}): Pick<
  PlatformUserDetailUpdateRequest,
  'businessOwnerType' | 'birthDate'
> => {
  if (
    form.businessOwnerType !== BUSINESS_OWNER_TYPE.INDIVIDUAL &&
    form.businessOwnerType !== BUSINESS_OWNER_TYPE.CORPORATE
  ) {
    return {};
  }
  if (form.businessOwnerType === BUSINESS_OWNER_TYPE.CORPORATE) {
    return { businessOwnerType: BUSINESS_OWNER_TYPE.CORPORATE };
  }
  const birthDate = form.birthDate.trim();
  return {
    businessOwnerType: BUSINESS_OWNER_TYPE.INDIVIDUAL,
    ...(birthDate ? { birthDate } : {}),
  };
};
